import { JSONParser, TokenType } from '@streamparser/json';

// A chooser hint, not a project decoder. Never hydrate graphs or migrate storage
// just to decide whether a recorded attempt belongs in the ordinary list.
const HEADER = 16;
const MAX_MANIFEST = 1024 * 1024;
const MAX_COMPRESSED_PREFIX = MAX_MANIFEST + 64 * 1024;
const rawMagic = /^DGTLCMP([2-6])$/;

function manifestLength(bytes: Uint8Array): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const length = view.getUint32(8, true);
  if (view.getUint32(12, true) || !length || length > MAX_MANIFEST)
    throw Error('Unknown project identity');
  return length;
}

/** Stops after the bounded manifest prefix, even for large compressed projects.
 * The payload/checksum is intentionally not validated: this read-only hint never
 * grants permission to open or modify a project. All uncertain inputs stay visible. */
async function manifestPrefix(file: Blob, signal?: AbortSignal): Promise<Uint8Array> {
  const header = new Uint8Array(await file.slice(0, HEADER).arrayBuffer());
  signal?.throwIfAborted();
  const magic = new TextDecoder().decode(header.subarray(0, 8));
  if (rawMagic.test(magic)) {
    const length = manifestLength(header);
    if (file.size < HEADER + length) throw Error('Truncated project manifest');
    return new Uint8Array(await file.slice(0, HEADER + length).arrayBuffer());
  }
  if (
    !/^DGTLZLB[56]$/.test(magic) ||
    header.length !== HEADER ||
    typeof DecompressionStream !== 'function'
  )
    throw Error('Unknown project format');
  const outer = new DataView(header.buffer);
  const expanded = outer.getUint32(8, true);
  if (outer.getUint32(12, true) || expanded < HEADER) throw Error('Invalid expansion');
  const stream = new DecompressionStream('deflate');
  const reader = stream.readable.getReader();
  const writer = stream.writable.getWriter();
  let stopped = false;
  const stop = () => {
    stopped = true;
    return Promise.allSettled([reader.cancel(), writer.abort()]);
  };
  const onAbort = () => {
    void stop();
  };
  signal?.addEventListener('abort', onAbort, { once: true });
  const pump = (async () => {
    for (let offset = HEADER; offset < file.size && !stopped; offset += 4096) {
      signal?.throwIfAborted();
      if (offset - HEADER >= MAX_COMPRESSED_PREFIX)
        throw Error('Compressed prefix budget');
      const bytes = await file
        .slice(offset, Math.min(offset + 4096, HEADER + MAX_COMPRESSED_PREFIX))
        .arrayBuffer();
      if (stopped) return;
      await writer.write(new Uint8Array(bytes));
    }
    if (!stopped) await writer.close();
  })().catch(async () => {
    await reader.cancel().catch(() => {});
  });
  let bytes = new Uint8Array(HEADER);
  let size = 0;
  let wanted = HEADER;
  try {
    while (true) {
      signal?.throwIfAborted();
      const { done, value } = await reader.read();
      signal?.throwIfAborted();
      if (done) throw Error('Truncated compressed manifest');
      let consumed = 0;
      if (wanted === HEADER) {
        const take = Math.min(value.length, HEADER - size);
        bytes.set(value.subarray(0, take), size);
        size += take;
        consumed = take;
        if (size < HEADER) continue;
        const innerMagic = new TextDecoder().decode(bytes.subarray(0, 8));
        if (innerMagic !== magic.replace('ZLB', 'CMP')) throw Error('Unexpected payload');
        wanted += manifestLength(bytes);
        if (wanted > expanded) throw Error('Invalid manifest length');
        const manifest = new Uint8Array(wanted);
        manifest.set(bytes);
        bytes = manifest;
      }
      const take = Math.min(value.length - consumed, wanted - size);
      bytes.set(value.subarray(consumed, consumed + take), size);
      size += take;
      if (size === wanted) return bytes;
    }
  } finally {
    await stop();
    await pump;
    signal?.removeEventListener('abort', onAbort);
    reader.releaseLock();
    writer.releaseLock();
  }
}

export async function inspectStoredProjectIdentity(
  raw: unknown,
  signal?: AbortSignal,
): Promise<string | undefined> {
  try {
    signal?.throwIfAborted();
    if (!(raw instanceof Blob)) return undefined;
    const bytes = await manifestPrefix(raw, signal);
    signal?.throwIfAborted();
    const version = Number(new TextDecoder().decode(bytes.subarray(0, 8)).at(-1));
    const parser = new JSONParser({ keepStack: true });
    const keys = new WeakMap<object, Set<string | number>>();
    let result: any;
    let depth = 0;
    parser.onToken = ({ token }) => {
      if (token === TokenType.LEFT_BRACE || token === TokenType.LEFT_BRACKET) {
        if (++depth > 128) throw Error('Manifest nesting limit');
      } else if (token === TokenType.RIGHT_BRACE || token === TokenType.RIGHT_BRACKET)
        --depth;
    };
    parser.onValue = ({ value, key, parent, stack }) => {
      if (parent && !Array.isArray(parent)) {
        const seen = keys.get(parent) ?? new Set<string | number>();
        if (seen.has(key!)) throw Error('Ambiguous project metadata');
        seen.add(key!);
        keys.set(parent, seen);
      }
      if (!stack.length) result = value;
    };
    parser.write(bytes.subarray(HEADER));
    if (!parser.isEnded) parser.end();
    if (result?.version !== version) return undefined;
    const project = result.project;
    const id = project?.mainChipId;
    // Scalar restoration must not override this identity hint.
    if (JSON.stringify(result.metadataScalars ?? []).includes('mainChipId'))
      return undefined;
    if (typeof id !== 'string' || !id || !Array.isArray(project.chips)) return undefined;
    if (project.chips.filter((chip: any) => chip?.id === id).length !== 1)
      return undefined;
    return id;
  } catch {
    signal?.throwIfAborted();
    return undefined;
  }
}
